import { TransferAuditTimelineAction, TransferStatus } from "../enums";
import { endpointsMetadata } from "../infrastructure/routes/endpointsMetadata";
import { IHistoryConfig } from "../components/history/models/historyModels";

export const TRANSFER_HISTORY_CUTOFF_DATE = new Date("2026-04-12");

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatNumber(value: unknown): string {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toFixed(3).replace(/\.?0+$/, "");
}

function formatDate(value: unknown): string {
  if (value === null || value === undefined || value === "") return "–";
  try {
    return new Date(String(value)).toLocaleDateString("bg-BG");
  } catch {
    return String(value);
  }
}

function formatStatusLabel(value: unknown): string {
  const n = Number(value);
  if (n === TransferStatus.Planned) return "Планирана";
  if (n === TransferStatus.Completed) return "Завършена";
  return String(value ?? "–");
}

export function generateTransferAuditText(
  action: number,
  payload: Record<string, any> | null | undefined,
  actorDisplayName: string | null | undefined,
): string {
  const actor = escapeHtml(actorDisplayName ?? "Системата");
  const p = payload ?? {};

  switch (action as TransferAuditTimelineAction) {
    case TransferAuditTimelineAction.TransferCreated: {
      const num = p.displayNumber
        ? escapeHtml(String(p.displayNumber))
        : "доставка";
      const lines = p.lineCount !== undefined ? ` (${p.lineCount} реда)` : "";
      return `${actor} създаде ${num}${lines}`;
    }
    case TransferAuditTimelineAction.TransferEdited:
      return `${actor} редактира доставката`;
    case TransferAuditTimelineAction.TransferDeleted:
      return `${actor} изтри доставката`;
    case TransferAuditTimelineAction.TransferRestored:
      return `${actor} възстанови доставката`;

    case TransferAuditTimelineAction.StatusChangedToCompleted:
      return `${actor} маркира доставката като завършена`;
    case TransferAuditTimelineAction.StatusChangedToPlanned:
      return `${actor} върна доставката към статус "Планирана"`;

    case TransferAuditTimelineAction.LineAdded: {
      const name = p.productName
        ? escapeHtml(String(p.productName))
        : `продукт #${p.inventoryItemId}`;
      return `${actor} добави ред: ${name}, ${formatNumber(p.quantity)}`;
    }
    case TransferAuditTimelineAction.LineRemoved: {
      const name = p.productName
        ? escapeHtml(String(p.productName))
        : `продукт #${p.inventoryItemId}`;
      return `${actor} премахна ред: ${name}, ${formatNumber(p.quantity)}`;
    }
    case TransferAuditTimelineAction.LineQuantityChanged: {
      const name = p.productName
        ? escapeHtml(String(p.productName))
        : `продукт #${p.inventoryItemId}`;
      return `${actor} промени количеството на ${name} от ${formatNumber(p.oldValue)} на ${formatNumber(p.newValue)}`;
    }
    case TransferAuditTimelineAction.LineExpirationDateChanged: {
      const name = p.productName
        ? escapeHtml(String(p.productName))
        : `продукт #${p.inventoryItemId}`;
      return `${actor} промени срока на годност за ${name}: ${formatDate(p.oldValue)} → ${formatDate(p.newValue)}`;
    }
    case TransferAuditTimelineAction.LineProductChanged: {
      const oldName = p.oldProductName
        ? escapeHtml(String(p.oldProductName))
        : `#${p.oldInventoryItemId}`;
      const newName = p.newProductName
        ? escapeHtml(String(p.newProductName))
        : `#${p.newInventoryItemId}`;
      return `${actor} смени продукт на ред: ${oldName} → ${newName}`;
    }

    case TransferAuditTimelineAction.DeliveryDateChanged:
      return `${actor} промени датата на доставка: ${formatDate(p.oldValue)} → ${formatDate(p.newValue)}`;
    case TransferAuditTimelineAction.SupplierChanged: {
      const oldVal = p.oldValue ? escapeHtml(String(p.oldValue)) : "–";
      const newVal = p.newValue ? escapeHtml(String(p.newValue)) : "–";
      return `${actor} промени доставчика: ${oldVal} → ${newVal}`;
    }

    case TransferAuditTimelineAction.NoteAdded:
      return `${actor} добави бележка`;
    case TransferAuditTimelineAction.NoteEdited:
      return `${actor} редактира бележка`;
    case TransferAuditTimelineAction.NoteDeleted:
      return `${actor} изтри бележка`;

    default:
      return `${actor} извърши действие (${action})`;
  }
}

export type TransferAuditCategory = "lifecycle" | "status" | "lines" | "notes";

export function getTransferAuditActionCategory(
  action: number,
): TransferAuditCategory {
  const a = action as TransferAuditTimelineAction;

  if (
    [
      TransferAuditTimelineAction.TransferCreated,
      TransferAuditTimelineAction.TransferEdited,
      TransferAuditTimelineAction.TransferDeleted,
      TransferAuditTimelineAction.TransferRestored,
      TransferAuditTimelineAction.DeliveryDateChanged,
      TransferAuditTimelineAction.SupplierChanged,
    ].includes(a)
  )
    return "lifecycle";

  if (
    [
      TransferAuditTimelineAction.StatusChangedToCompleted,
      TransferAuditTimelineAction.StatusChangedToPlanned,
    ].includes(a)
  )
    return "status";

  if (
    [
      TransferAuditTimelineAction.LineAdded,
      TransferAuditTimelineAction.LineRemoved,
      TransferAuditTimelineAction.LineQuantityChanged,
      TransferAuditTimelineAction.LineExpirationDateChanged,
      TransferAuditTimelineAction.LineProductChanged,
    ].includes(a)
  )
    return "lines";

  return "notes";
}

export const TRANSFER_AUDIT_CATEGORY_COLORS: Record<
  TransferAuditCategory,
  string
> = {
  lifecycle: "#6366f1",
  status: "#f59e0b",
  lines: "#10b981",
  notes: "#3b82f6",
};

const dot = (color: string) =>
  `<span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${color};margin-right:8px;vertical-align:middle;flex-shrink:0"></span>`;

export const TRANSFER_AUDIT_COLOR_LEGEND_HTML = `
<div style="line-height:2;min-width:180px">
  <div>${dot("#6366f1")}Доставка</div>
  <div>${dot("#f59e0b")}Статус</div>
  <div>${dot("#10b981")}Продукти</div>
  <div>${dot("#3b82f6")}Бележки</div>
</div>`.trim();

export const TRANSFER_HISTORY_CONFIG: IHistoryConfig = {
  historyEndpoint: endpointsMetadata.inventoryTransfers.getTransferHistory,
  historyQueryParam: "transferId",
  detailEndpoint: endpointsMetadata.inventoryTransfers.getHistoryDetail,
  cutoffDate: TRANSFER_HISTORY_CUTOFF_DATE,
  cutoffDateLabel: "12.04.2026 г.",
  tooltipHtml: TRANSFER_AUDIT_COLOR_LEGEND_HTML,
  generateText: generateTransferAuditText,
  getCategory: getTransferAuditActionCategory,
  categoryColors: TRANSFER_AUDIT_CATEGORY_COLORS,
};
